import { useEffect, useState, type ReactNode } from 'react';

import {
  messageFromSuccessBody,
  REQUEST_ERROR_EVENT,
  REQUEST_SUCCESS_EVENT,
  type RequestSuccessInfo,
} from './request-success.ts';

interface ToastItem {
  readonly id: number;
  readonly message: string;
  readonly tone: 'success' | 'error';
}

const TOAST_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastItem[]>([]);

  useEffect(() => {
    const show = (message: string, tone: 'success' | 'error') => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, message, tone }]);
      window.setTimeout(() => {
        setToasts((current) => current.filter((t) => t.id !== id));
      }, TOAST_MS);
    };

    const onSuccess = (event: Event) => {
      const detail = (event as CustomEvent<RequestSuccessInfo>).detail;
      const message = messageFromSuccessBody(detail.body) ?? 'Saved.';
      show(message, 'success');
    };

    const onError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      show(detail?.message ?? 'That did not go through.', 'error');
    };

    window.addEventListener(REQUEST_SUCCESS_EVENT, onSuccess);
    window.addEventListener(REQUEST_ERROR_EVENT, onError);
    return () => {
      window.removeEventListener(REQUEST_SUCCESS_EVENT, onSuccess);
      window.removeEventListener(REQUEST_ERROR_EVENT, onError);
    };
  }, []);

  return (
    <>
      {children}
      <div className="toast-stack">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast-${t.tone}`}
            role={t.tone === 'error' ? 'alert' : 'status'}
            aria-live={t.tone === 'error' ? 'assertive' : 'polite'}
          >
            {t.message}
          </div>
        ))}
      </div>
    </>
  );
}
